import { PLATFORM } from 'config/constants';
import { IPlatform, GenericObject } from 'core/models/platform';
import { DocumentContext } from 'next/document';
import logger from 'core/logger/logger';
import { splitIntoSentences } from './sentenceSplitter';
import QuillLocalStorage from './localStorageClient';
// import CONSTANTS from 'src/paraphraser/constants';
// import stops from './stops.json';

// const stopWords: { [key: string]: boolean } = {};
// CONSTANTS.STOP_WORDS.forEach((s: string) => {
//   stopWords[s] = true;
// });

// export { stopWords };

export const detectAgent = (ctx?: DocumentContext): IPlatform => {
  const navigatorAgent =
    process.browser && navigator && navigator.userAgent ? navigator.userAgent : '';
  const userAgent = ctx?.req?.headers?.['user-agent'] || navigatorAgent;
  const headers = ctx?.req?.headers || {};

  const isPlatform = (regex: RegExp, header: string): boolean =>
    Boolean(userAgent?.match(regex) || headers[header]);

  const isAndroid = isPlatform(PLATFORM.ANDROID, 'is-android');
  const isIos = isPlatform(PLATFORM.IOS, 'is-ios');
  const isIPhone = isPlatform(PLATFORM.IPHONE, 'is-iphone');
  const isOperaMobile = isPlatform(PLATFORM.OPERA_MINI, 'is-opera-mobile');
  const isChrome = isPlatform(PLATFORM.CHROME, 'is-chrome');
  const isEdge = isPlatform(PLATFORM.EDGE, 'is-edge');
  const isOpera = isPlatform(PLATFORM.OPERA, 'is-opera');
  const isFirefox = isPlatform(PLATFORM.FIREFOX, 'is-firefox');
  let isSafari = isPlatform(PLATFORM.SAFARI, 'is-safari');
  const isIE = isPlatform(PLATFORM.IE, 'is-ie');
  const isMac = isPlatform(PLATFORM.MAC, 'is-mac');
  const isTablet = isPlatform(PLATFORM.TABLET, 'is-tablet');

  // Chrome user agent contains safari hence it is matching the regex set for Safari,
  // i.e. /SAFARI/i in webapp code. The same regex is used in worker as well.
  if (isChrome && isSafari) {
    isSafari = false;
  }

  const isMobile = isAndroid || isIPhone || isOperaMobile || Boolean(headers['is-mobile']);
  const isDesktop = !isMobile;

  return {
    isMobile,
    isDesktop,
    isAndroid,
    isIos,
    isIPhone,
    isOperaMobile,
    isChrome,
    isEdge,
    isOpera,
    isFirefox,
    isSafari,
    isIE,
    isMac,
    isTablet,
  };
};

export const detectLanguage = (ctx?: DocumentContext): string => {
  let language = '#NA';

  if (!process.browser) {
    const headerLang = ctx?.req?.headers['accept-language'];
    if (headerLang) {
      if (headerLang?.includes(',')) language = headerLang.split(',')[0];
      else language = headerLang;
    }
  }

  if (process.browser && navigator.language) {
    language = navigator.language;
  }

  return language;
};

export const validEmail = (email: string) => {
  if (!email) return false;
  const emailRegex = /\S+@\S+\.\S+/; // ^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return email.match(emailRegex);
};

export const getGUID = () => {
  function s4() {
    return Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  }
  return s4() + s4() + s4() + s4();
};

export function getClipboardData() {
  return new Promise<string>((resolve, reject) => {
    if (!navigator || !navigator.clipboard || !navigator.clipboard.readText) {
      reject('Unable to paste, please paste manually');
    }
    navigator.clipboard
      .readText()
      .then((text: string) => resolve(text))
      .catch(() => reject('Paste permission denied!'));
  });
}

export const serializeQueryParams = (paramsObj: GenericObject) => {
  let result = '';
  Object.keys(paramsObj).forEach((key) => {
    result += `${key}=${encodeURIComponent(paramsObj[key])}&`;
  });
  return result.substr(0, result.length - 1);
};

export const appLoaded = (callback: VoidFunction) => {
  if (!process.browser) return;
  if (document.readyState === 'complete') callback();
  else window.addEventListener('load', callback);
};

export const isAlphaNumeric = (str: string) => str.match(/^[A-Za-z0-9]+$/);

export const isEmail = (str: string) => str.match(/\S+@\S+\.\S+/);

export const currencyFormatted = (amount: string) => {
  let i = parseFloat(amount);

  if (!i) i = 0.0;

  let minus = '';
  if (i < 0) {
    minus = '-';
  }
  i = Math.abs(i);
  i = parseInt((i + 0.005).toString(), 10);
  i /= 100;
  let s = `${i}`;
  if (s.indexOf('.') < 0) {
    s += '.00';
  }
  if (s.indexOf('.') === s.length - 2) {
    s += '0';
  }
  s = minus + s;
  return s;
};

export const replaceAll = (str: string, search: RegExp, replacement: string) =>
  str.replace(new RegExp(search, 'g'), replacement);

// export function nonStopWords(str: string) {
//   return str.split(' ').filter((w: string) => !(stopWords[w] !== undefined));
// }

export const removeNullOrUndefined = (obj: GenericObject) =>
  Object.keys(obj).forEach((key) =>
    obj[key] === undefined || obj[key] === null ? delete obj[key] : {}
  );

export const removeEmptyStrings = (obj: GenericObject) =>
  Object.keys(obj).forEach((key) => (obj[key] === '' ? delete obj[key] : {}));

export function GUID() {
  function s4() {
    return Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  }
  return s4() + s4() + s4() + s4();
}

export function clone(obj: object) {
  return JSON.parse(JSON.stringify(obj));
}

/**
 * calls google tag manager with values
 *
 * @param {string} category category of the action
 * @param {string} action the specific action taken
 * @param {string | object} label a string or object that will be stringified to store as data
 */
export function gEvent(category: string, action: string, gLabel: string | Object) {
  const vals: GenericObject = {};
  let label = gLabel;

  if (typeof label === 'object') {
    label = JSON.stringify(label);
  }
  vals.event_label = label || '';
  vals.event_category = category || '';

  // @TODO: reimplement window
  const gtag = window.gtag;
  try {
    window.gtag('event', action, JSON.stringify(vals));
  } catch (err: any) {
    if (err.message === 'gtag is not defined') {
      setTimeout(() => {
        try {
          gtag('event', action, JSON.stringify(vals));
        } catch (e) {
          logger('GTAGERROR', e);
        }
      }, 1000);
    }
  }
}

export function makeSerial(obj: GenericObject, ignore: string[]) {
  const data: GenericObject = {};
  Object.keys(obj).forEach((key) => {
    const property: string = <string>obj[key];
    if (!ignore.includes(property)) {
      if (Object.prototype.hasOwnProperty.call(obj, property)) {
        const prop = obj[property] as GenericObject;
        const type = typeof prop;

        if (type !== 'function') {
          if (type === 'object' && prop && prop.serialize !== undefined) {
            data[property] = prop.serialize();
          } else if (prop instanceof Array) {
            data[property] = prop.map((p) => {
              const ptype = typeof p;
              if (ptype === 'function') return '[FUNCTION]';
              if (ptype === 'object') {
                if (p && p.serialize !== undefined) {
                  return p.serialize();
                }
                return makeSerial(p, []);
              }
              return p;
            });
          } else data[property] = prop;
        }
      }
    }
  });

  return data;
}

export function longestCommonSubstring(string1: string, string2: string) {
  const comparsions: number[][] = []; // 2D array for the char comparsions ...
  let maxSubStrLength = 0;
  let lastMaxSubStrIndex = -1;
  let i;
  let j;
  let char1;
  let char2;
  let startIndex;

  for (i = 0; i < string1.length; i += 1) {
    comparsions[i] = [];

    for (j = 0; j < string2.length; j += 1) {
      char1 = string1.charAt(i);
      char2 = string2.charAt(j);

      if (char1 === char2) {
        if (i > 0 && j > 0) {
          comparsions[i][j] = comparsions[i - 1][j - 1] + 1;
        } else {
          comparsions[i][j] = 1;
        }
      } else {
        comparsions[i][j] = 0;
      }

      if (comparsions[i][j] > maxSubStrLength) {
        maxSubStrLength = comparsions[i][j];
        lastMaxSubStrIndex = i;
      }
    }
  }

  if (maxSubStrLength > 0) {
    startIndex = lastMaxSubStrIndex - maxSubStrLength + 1;

    return string1.substr(startIndex, maxSubStrLength);
  }

  return '';
}

export function copyTextToClipboard(text: string, success: Function, failure: Function) {
  const textArea: HTMLTextAreaElement = document.createElement('textarea');

  // Place in top-left corner of screen regardless of scroll position.
  textArea.style.position = 'fixed';
  textArea.style.top = '0';
  textArea.style.left = '0';

  // Ensure it has a small width and height. Setting to 1px / 1em
  // doesn't work as this gives a negative w/h on some browsers.
  textArea.style.width = '2em';
  textArea.style.height = '2em';

  // We don't need padding, reducing the size if it does flash render.
  textArea.style.padding = '0';

  // Clean up any borders.
  textArea.style.border = 'none';
  textArea.style.outline = 'none';
  textArea.style.boxShadow = 'none';

  // Avoid flash of white box if rendered for any reason.
  textArea.style.background = 'transparent';

  textArea.value = text;

  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  try {
    const successful = document.execCommand('copy');
    if (successful && success) {
      success();
    } else {
      throw Object.assign(new Error('Copy Failed'), { code: 204 });
    }
  } catch (err) {
    logger('COPY ERROR', 'Oops, unable to copy');
    if (failure) {
      failure();
    }
  }

  document.body.removeChild(textArea);
}

function selectElementContents(el: HTMLElement) {
  if (window.getSelection && document.createRange) {
    const range = document.createRange();
    range.selectNodeContents(el);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  }
}

export function copyRichTextToClip(str: string, success: Function, failure: Function) {
  const div = document.createElement('div');
  div.innerHTML = str;

  // Place in top-left corner of screen regardless of scroll position.
  div.style.position = 'fixed';
  div.style.top = '0';
  div.style.left = '0';
  div.style.zIndex = '-100000000000';

  // Ensure it has a small width and height. Setting to 1px / 1em
  // doesn't work as this gives a negative w/h on some browsers.
  div.style.width = '2em';
  div.style.height = '2em';

  // We don't need padding, reducing the size if it does flash render.
  div.style.padding = '0';

  // Clean up any borders.
  div.style.border = 'none';
  div.style.outline = 'none';
  div.style.boxShadow = 'none';

  // Avoid flash of white box if rendered for any reason.
  div.style.background = 'transparent';
  // div.contentEditable = 'true';
  (div as any).readOnly = 'false';

  document.body.appendChild(div);
  const text = div.textContent || div.innerText;
  const listener = (e: any) => {
    if (e && e.clipboardData) {
      e.clipboardData.setData('text/plain', text);
      e.clipboardData.setData('text/html', str);
      e.preventDefault();
    } else {
      document.removeEventListener('copy', listener);
      copyTextToClipboard(text, success, failure);
    }
    document.removeEventListener('copy', listener);
  };
  try {
    selectElementContents(div);
    document.addEventListener('copy', listener);
    const successful = document.execCommand('copy');
    document.removeEventListener('copy', listener);
    if (successful && success) {
      success();
    } else {
      throw Object.assign(new Error('Copy Failed'), { code: 204 });
    }
  } catch (err) {
    logger('COPY ERROR', 'Oops, unable to copy');
    if (failure) {
      failure();
    }
  }
  setTimeout(() => {
    document.body.removeChild(div);
  }, 0);
}

export function applyBreakpoint(
  width: string,
  breakpointObject: GenericObject,
  defaultValue: number
) {
  const orderedBreakpoints = ['xs', 'sm', 'md', 'lg', 'xl'];
  if (breakpointObject[width]) return breakpointObject[width];

  for (let i = orderedBreakpoints.indexOf(width) - 1; i >= 0; i -= 1) {
    if (breakpointObject[orderedBreakpoints[i]]) {
      return breakpointObject[orderedBreakpoints[i]];
    }
  }
  return defaultValue;
}

export function getSelectionBoundaryElement(isStart: boolean) {
  let range;
  const sel = window.getSelection();
  let container;

  if (document.selection) {
    range = document.selection.createRange();
    range.collapse(isStart);
    return range.parentElement();
  }

  if (sel?.getRangeAt) {
    if (sel.rangeCount > 0) {
      range = sel.getRangeAt(0);
    }
  } else {
    // Old WebKit
    range = document.createRange();
    if (sel != null) {
      range.setStart(<Node>sel.anchorNode, sel.anchorOffset);
      range.setEnd(<Node>sel.focusNode, sel.focusOffset);
    }
    // Handle the case when the selection was selected backwards (from the end to the start in the document)
    if (sel && range.collapsed !== sel.isCollapsed) {
      range.setStart(<Node>sel.focusNode, sel.focusOffset);
      range.setEnd(<Node>sel.anchorNode, sel.anchorOffset);
    }
  }

  if (range) {
    container = range[isStart ? 'startContainer' : 'endContainer'];

    // Check if the container is a text node and return its parent if so
    return container.nodeType === 3 ? container.parentNode : container;
  }
  return '';
}

export function removePunctutation(inputText: string, requireSmallcase = false) {
  const text = inputText.replace(/[^\w\s]|_/g, '');
  if (requireSmallcase) {
    return text.toLowerCase();
  }
  return text;
}

export function isElementInViewport(el: unknown, topOffset = 0, bottomOffset = 0) {
  const rect = (<HTMLElement>el).getBoundingClientRect();
  return (
    rect.top >= 0 + topOffset &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) - bottomOffset &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
}

export function getPxFromVh(vh: number, defaultInnerHeight?: number) {
  const viewportHeight =
    defaultInnerHeight || window.innerHeight || document.documentElement.clientHeight;
  return (vh * viewportHeight) / 100;
}

export function insertTextAtCursor(text: string) {
  let sel;
  let range;

  if (window.getSelection) {
    sel = window.getSelection();
    if (sel?.getRangeAt && sel.rangeCount) {
      range = sel.getRangeAt(0);
      range.deleteContents();
      const textNode = document.createTextNode(text);
      range.insertNode(textNode);
      range.setStartAfter(textNode);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  } else if (document.selection && document.selection.createRange) {
    document.selection.createRange().text = text;
  }
}

export function getWidthBreakPoint() {
  const windowWidth = window.innerWidth;
  const breakpointLabels = ['xs', 'sm', 'md', 'lg', 'xl'];
  const breakpointWidths = [0, 600, 960, 1280, 1920];
  for (let i = 4; i >= 0; i -= 1) {
    if (windowWidth >= breakpointWidths[i]) {
      return breakpointLabels[i];
    }
  }
  return 'xs';
}

export function parseCookies(pairs: string | undefined) {
  return pairs
    ?.split(';')
    .reduce(
      (ac: Record<string, string>, str: string) =>
        Object.assign(ac, { [str.split('=')[0].trim()]: str.split('=')[1] }),
      {}
    );
}
// export function getClipboardData() {
//   return new Promise((resolve, reject) => {
//     if (!navigator || !navigator.clipboard) {
//       reject("Couldn't paste, Please paste manually");
//     }
//     navigator.clipboard
//       .readText()
//       .then((text) => {
//         resolve(text);
//       })
//       .catch(() => {
//         reject('Paste permission denied!');
//       });
//   });
// }

export function getExpandedModuleNames(moduleName: string): string {
  if (!moduleName || moduleName === '') return '';
  const moduleNameMap: { [key: string]: string } = {
    pphr: 'Paraphraser',
    smzr: 'Summarizer',
  };
  return moduleNameMap[moduleName] || '';
}

export function getLabelForPremiumPlanCode(planCode: number): 'Monthly' | 'Semi-Annual' | 'Annual' {
  const code = planCode - 1;
  const planArr = ['Monthly', 'Semi-Annual', 'Annual'];
  if (code >= 0 && code < planArr.length) {
    return planArr[code] as 'Monthly' | 'Semi-Annual' | 'Annual';
  }
  return 'Monthly';
}

export function getLabelForPremiumDuration(duration: number): 'Monthly' | 'Semi-Annual' | 'Annual' {
  switch (duration) {
    case 6:
      return 'Semi-Annual';
    case 12:
      return 'Annual';
    default:
      return 'Monthly';
  }
}

export function getParaphraserModeLabel(strength: number) {
  const mappingWithStrength = [
    'Fluency',
    'Concision',
    'Standard',
    'Suggestive',
    'Creative',
    'Flip more words',
    'Creative',
    'Shorten',
    'Expand',
    'Formal',
    'Simple',
  ];
  return mappingWithStrength[strength];
}

export function timeout(t: number) {
  return new Promise((res) => {
    setTimeout(() => {
      res('TIMEOUT');
    }, t);
  });
}

export const numberFormatter = (value: number, commaOnly?: boolean): string => {
  if (commaOnly && value < 100000) {
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  if (value < 10000) {
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  if (value < 1000000) {
    return `${(value / 1000).toFixed(0)}k`;
  }
  return `${(value / 1000000).toFixed(0)}m`;
};

export const capitalize = (text: string) => text?.charAt(0).toUpperCase() + text?.slice(1);

export const getStats = (para: string) => {
  const sentences = splitIntoSentences(para).filter((sentence) => sentence.type === 'Sentence');
  const statsArray: string[] = [];
  sentences.forEach((sentence) => {
    if (/\s\d/.test(sentence.raw)) {
      statsArray.push(sentence.raw);
    }
  });
  return statsArray;
};

export const debouncedFn = (fn: Function, delay: number, context?: any) => {
  let timer: ReturnType<typeof setTimeout>;

  return (...args: unknown[]) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      fn.call(context, ...args);
    }, delay);
  };
};

export const generateRandomId = (): string => Math.random().toString(36).substring(2, 15);

export const getRandomInt = (max: number): number => Math.floor(Math.random() * max);

export const parseBoolean = (operator: string) => {
  if (!operator) return false;
  switch (operator.toLowerCase().trim()) {
    case 'true':
    case 'yes':
    case '1':
      return true;
    case 'false':
    case 'no':
    case '0':
    case null:
      return false;
    default:
      return Boolean(operator);
  }
};

export function getShortUrl(url: string) {
  return url
    .slice(url.indexOf('//') + 2)
    .split('/')[0]
    .replace(/^www./, '');
}

export const safeJSONParse = (json: string, defaultVaule?: any) => {
  try {
    return JSON.parse(json);
  } catch (e) {
    console.log('Unable to parse json', e);
    return defaultVaule;
  }
};

export const safeJSONStringify = (value: any, defaultValue: any) => {
  try {
    return JSON.stringify(value);
  } catch (e) {
    console.log('Unable to stringify json', e);
    return defaultValue;
  }
};

/** Replace all double and single quotes */
export const replaceQuotes = (str: string) => str.replace(/["']/g, '');

export const sha256 = async (message: string, key?: string): Promise<string> => {
  try {
    if (key) {
      const savedMsg = QuillLocalStorage.getInstance().get(key);
      if (savedMsg) return savedMsg;
    }

    if (!message) return '';

    const utf8 = new TextEncoder().encode(message);
    const hashBuffer = await crypto.subtle.digest('SHA-256', utf8);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map((bytes) => bytes.toString(16).padStart(2, '0')).join('');

    if (key) QuillLocalStorage.getInstance().set(key, hashHex);

    return hashHex;
  } catch (e) {
    return '';
  }
};
